import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Loader2, CheckCircle, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../../utils/tokenManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ISSUE_LABELS = {
  indirect_no_entity: 'Indirect investment missing entity',
  indirect_orphaned_entity: 'Indirect investment references non-existent entity',
  related_orphaned_entity: 'Related investment references non-existent entity/asset',
  empty_entity: 'Entity with zero linked investments',
  empty_related_asset: 'Related asset with zero linked investments',
};

const FIX_LABELS = {
  'Convert to direct': 'Will convert to direct ownership',
  'Soft-delete entity': 'Will soft-delete this entity',
  'Soft-delete asset': 'Will soft-delete this asset',
};

export function IntegrityReportModal({ report, onClose, onApplyFixes, applying }) {
  if (!report) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700">
          <div>
            <h3 className="text-lg font-bold text-white">Data Integrity Report</h3>
            <p className="text-sm text-gray-400 mt-1">{report.user_name} ({report.user_email})</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="integrity-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Summary */}
        <div className="p-4 sm:p-6 border-b border-gray-700">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-gray-900 rounded-lg p-3">
              <p className="text-xl font-bold text-white">{report.total_investments}</p>
              <p className="text-xs text-gray-400">Investments</p>
            </div>
            <div className="bg-gray-900 rounded-lg p-3">
              <p className="text-xl font-bold text-white">{report.total_entities}</p>
              <p className="text-xs text-gray-400">Entities</p>
            </div>
            <div className={`rounded-lg p-3 ${report.issue_count > 0 ? 'bg-amber-900/30 border border-amber-700/50' : 'bg-emerald-900/30 border border-emerald-700/50'}`}>
              <p className={`text-xl font-bold ${report.issue_count > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>{report.issue_count}</p>
              <p className={`text-xs ${report.issue_count > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>Issues Found</p>
            </div>
          </div>
        </div>

        {/* Issues List */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {report.issue_count === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-white font-semibold">No issues found</p>
              <p className="text-sm text-gray-400 mt-1">This user's data is clean.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-300 mb-4">The following issues were detected. Review and click "Apply Fixes" to resolve them.</p>
              {report.issues.map((issue, i) => (
                <div key={`issue-${i}`} className="bg-gray-900 rounded-lg p-3 border border-gray-700" data-testid={`integrity-issue-${i}`}>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium">
                        {issue.investment_name || issue.entity_name || issue.asset_name}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {ISSUE_LABELS[issue.type] || issue.description}
                      </p>
                      <p className="text-xs text-blue-400 mt-1">
                        Fix: {FIX_LABELS[issue.fix] || issue.fix}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-gray-700 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} className="text-gray-300 border-gray-600 hover:bg-gray-700">
            {report.issue_count === 0 ? 'Close' : 'Cancel'}
          </Button>
          {report.issue_count > 0 && (
            <Button
              data-testid="integrity-apply-fixes-btn"
              onClick={onApplyFixes}
              disabled={applying}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Applying...</> : `Apply ${report.issue_count} Fix${report.issue_count !== 1 ? 'es' : ''}`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function useIntegrityCheck() {
  const [checking, setChecking] = useState(null);
  const [report, setReport] = useState(null);
  const [applying, setApplying] = useState(false);

  const runCheck = async (userId) => {
    setChecking(userId);
    setReport(null);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/integrity-check`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || 'Check failed'); return; }
      const data = await res.json();
      setReport(data);
    } catch {
      toast.error('Failed to run integrity check');
    } finally {
      setChecking(null);
    }
  };

  const applyFixes = async (fetchUsers) => {
    if (!report) return;
    setApplying(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/admin/users/${report.user_id}/integrity-fix`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) { const err = await res.json(); toast.error(err.detail || 'Fix failed'); return; }
      const data = await res.json();
      toast.success(data.message);
      setReport(null);
      if (fetchUsers) fetchUsers();
    } catch {
      toast.error('Failed to apply fixes');
    } finally {
      setApplying(false);
    }
  };

  const closeReport = () => setReport(null);

  return { checking, report, applying, runCheck, applyFixes, closeReport };
}
